import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { X, Calendar, Clock, Award, Crown, Loader2, Coins, Minus, Plus, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { priceStore } from '../utils/priceStore';
import { orderService } from '../utils/orderService';
import { supabase, getSupabaseUrl } from '../supabase/client';
import { categoryStore } from '../store/categoryStore';
import { isCategoryMatch, SITE_WIDE, isSiteWidePackage } from '../utils/permissionCheck';
import AlipayPaymentModal from './AlipayPaymentModal';
import { getBeijingTimestamp } from '../utils/timeUtils';

interface Paper {
  id: string;
  title: string;
  organization: string;
  category_type: string;
  level_text: string;
}

interface PackagePurchaseModalProps {
  paper: Paper;
  onClose: () => void;
  role?: 'student' | 'teacher';
}

const durationConfigs = [
  { key: 'month', label: '月包', months: 1, icon: Calendar },
  { key: 'quarter', label: '季包', months: 3, icon: Clock },
  { key: 'halfYear', label: '半年包', months: 6, icon: Award },
  { key: 'year', label: '年包', months: 12, icon: Crown },
];

const POINTS_PER_YUAN = 100;

export default function PackagePurchaseModal({ paper, onClose, role = 'student' }: PackagePurchaseModalProps) {
  const navigate = useNavigate();
  const [paying, setPaying] = useState(false);
  const [showAlipayModal, setShowAlipayModal] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<any>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [packagePrice, setPackagePrice] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDuration, setSelectedDuration] = useState<string>('');
  const [error, setError] = useState('');

  // ===== 全站通套餐购买模式（与当前级别套餐并列） =====
  const [purchaseMode, setPurchaseMode] = useState<'level' | 'sitewide'>('level');
  const [siteWidePrices, setSiteWidePrices] = useState<{ month: number; quarter: number; halfYear: number; year: number } | null>(null);
  const isSiteWideMode = purchaseMode === 'sitewide';

  // U币抵扣相关状态
  const [totalPoints, setTotalPoints] = useState(0);
  const [usePoints, setUsePoints] = useState(true);
  const [pointsToUse, setPointsToUse] = useState(0);
  const [zeroPaymentProcessing, setZeroPaymentProcessing] = useState(false);

  const isTeacher = role === 'teacher';
  const themeColor = isTeacher ? 'purple' : 'blue';

  useEffect(() => {
    loadUserAndPrices();
  }, [paper, role]);

  const loadUserAndPrices = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
        setCurrentUser({ id: session.user.id, ...profile });

        // 查询U币余额
        try {
          const res = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ action: 'get_account', user_id: session.user.id, role }),
          });
          const result = await res.json();
          if (result.success && result.account) {
            setTotalPoints(result.account.total_points || 0);
          }
        } catch {}
      }

      // 统一通过 priceStore 加载价格（教师端与学生端一致）
      // priceStore 内部会同时匹配 code（如 "scratch"）和 name（如 "图形化编程"）
      const price = await priceStore.findPriceByNames(paper.organization, paper.category_type, paper.level_text);
      setPackagePrice(price);

      // 全站通独立定价：exam_settings.site_wide_prices 优先，未设置回退聚合口径（与后台定价页一致）
      try {
        await categoryStore.init();
        setSiteWidePrices(categoryStore.getSiteWidePrices());
      } catch {
        setSiteWidePrices(null);
      }
    } catch (err) {
      setError('加载数据失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (key: string) => {
    // 全站通模式：读独立定价（getSiteWidePrices 已含聚合回退）
    if (isSiteWideMode && siteWidePrices) {
      return (siteWidePrices as any)[key] || 0;
    }
    if (!packagePrice) return 0;
    switch (key) {
      case 'month': return packagePrice.monthPrice;
      case 'quarter': return packagePrice.quarterPrice;
      case 'halfYear': return packagePrice.halfYearPrice;
      case 'year': return packagePrice.yearPrice;
      default: return 0;
    }
  };

  const maxDeductionYuan = useMemo(() => Math.floor(totalPoints / POINTS_PER_YUAN), [totalPoints]);

  const currentPrice = useMemo(() => getPrice(selectedDuration), [selectedDuration, packagePrice]);

  const deductionAmount = useMemo(() => {
    if (!usePoints || pointsToUse <= 0) return 0;
    // 使用整数运算避免浮点精度问题：先算分，再转元
    return Math.floor(pointsToUse / POINTS_PER_YUAN * 100) / 100;
  }, [usePoints, pointsToUse]);

  const actualPrice = useMemo(() => {
    // 使用整数运算（分）避免浮点精度问题
    const priceInCents = Math.round(currentPrice * 100);
    const deductionInCents = Math.round(deductionAmount * 100);
    const actualInCents = Math.max(0, priceInCents - deductionInCents);
    return actualInCents / 100;
  }, [currentPrice, deductionAmount]);

  const isZeroPayment = useMemo(() => actualPrice <= 0 && pointsToUse > 0, [actualPrice, pointsToUse]);

  const handleDurationSelect = (key: string) => {
    setSelectedDuration(key);
    const price = getPrice(key);
    const maxPointsForOrder = Math.min(totalPoints, Math.floor(price * POINTS_PER_YUAN));
    setPointsToUse(maxPointsForOrder);
    setUsePoints(totalPoints > 0);
  };

  // 切换购买模式（当前级别 / 全站通）时重置档位与 U 币抵扣
  const handleModeSwitch = (mode: 'level' | 'sitewide') => {
    if (purchaseMode === mode) return;
    setPurchaseMode(mode);
    setSelectedDuration('');
    setPointsToUse(0);
    setUsePoints(false);
    setError('');
  };

  // 本单写入 user_packages/orders 的三列值（全站通模式下三列均为约定值「全站通」）
  const orderOrgName = isSiteWideMode ? SITE_WIDE : (paper.organization || '');
  const orderCatName = isSiteWideMode ? SITE_WIDE : (paper.category_type || '');
  const orderLevelText = isSiteWideMode ? SITE_WIDE : (paper.level_text || '');

  const adjustPoints = (delta: number) => {
    const maxPointsForOrder = Math.min(totalPoints, Math.floor(currentPrice * POINTS_PER_YUAN));
    const newPoints = Math.max(0, Math.min(maxPointsForOrder, pointsToUse + delta));
    setPointsToUse(newPoints);
  };

  const handlePurchase = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (!selectedDuration) return;

    const price = getPrice(selectedDuration);
    const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();

    // 重复购买检查
    if (isSiteWideMode) {
      // 全站通：精确匹配 organization = '全站通' 的有效套餐
      const { data: existingSiteWide } = await supabase
        .from('user_packages')
        .select('*')
        .eq('user_id', currentUser.id)
        .eq('is_active', true)
        .gte('expire_date', today)
        .eq('organization', SITE_WIDE)
        .eq('role', role);
      if ((existingSiteWide || []).some(isSiteWidePackage)) {
        setError('您已开通全站通刷题包，请勿重复购买');
        return;
      }
    } else {
    // 统一使用 user_packages 表检查重复购买
    // 使用宽松查询（仅精确匹配 org/level/role），再用 isCategoryMatch 兼容 code/name 格式
    const { data: existingPackages } = await supabase
      .from('user_packages')
      .select('*')
      .eq('user_id', currentUser.id)
      .eq('is_active', true)
      .gte('expire_date', today)
      .eq('organization', paper.organization)
      .eq('level_text', paper.level_text)
      .eq('role', role);

    const matchedExisting = (existingPackages || []).filter(pkg =>
      isCategoryMatch(pkg.category, paper.category_type)
    );

    if (matchedExisting.length > 0) {
      setError('您已开通该刷题包，请勿重复购买');
      return;
    }
    }

    setPaying(true);
    setError('');

    try {
      // 统一使用 createOrder，传入 role 参数（全站通模式下三列写约定值「全站通」）
      const order = await orderService.createOrder({
        userId: currentUser.id,
        userName: currentUser.username || currentUser.phone || '未知用户',
        userPhone: currentUser.phone,
        orgName: orderOrgName,
        catName: orderCatName,
        levelText: orderLevelText,
        duration: selectedDuration as 'month' | 'quarter' | 'halfYear' | 'year',
        price: price,
        source: 'package',
        pointsDeducted: usePoints ? pointsToUse : 0,
        deductionAmount: usePoints ? deductionAmount : 0,
        role,
      });

      setCurrentOrder(order);

      if (isZeroPayment) {
        setZeroPaymentProcessing(true);
        try {
          const session = await supabase.auth.getSession();
          const res = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.data.session?.access_token}`,
            },
            body: JSON.stringify({
              action: 'zero_payment',
              user_id: currentUser.id,
              order_id: order.id,
              order_no: order.orderNo,
              points_to_deduct: pointsToUse,
              deduction_amount: deductionAmount,
              role,
            }),
          });
          const result = await res.json();
          if (result.success) {
            setShowAlipayModal(false);
            window.dispatchEvent(new Event('package-purchased'));
            onClose();
            navigate(`/payment-result?status=success&orderNo=${order.orderNo}&pointsDeducted=${pointsToUse}&deductionAmount=${deductionAmount}`);
          } else {
            setError('U币支付失败：' + (result.error || '未知错误'));
          }
        } catch {
          setError('U币支付失败，请重试');
        } finally {
          setZeroPaymentProcessing(false);
          setPaying(false);
        }
      } else {
        setShowAlipayModal(true);
        setPaying(false);
      }
    } catch {
      setError('创建订单失败，请重试');
      setPaying(false);
    }
  };

  const handleAlipaySuccess = async () => {
    if (!currentOrder) return;

    // 幂等性检查：先查询订单状态，如果已经是 paid 则跳过通知
    const { data: orderCheck } = await supabase
      .from('orders')
      .select('status')
      .eq('id', currentOrder.id)
      .maybeSingle();

    const alreadyPaid = orderCheck?.status === 'paid';

    const durationKey = currentOrder.duration;
    const expireDate = new Date();
    const months = { month: 1, quarter: 3, halfYear: 6, year: 12 }[durationKey as 'month' | 'quarter' | 'halfYear' | 'year'] || 1;
    expireDate.setMonth(expireDate.getMonth() + months);

    // 统一写入 user_packages 表（使用 upsert 防止重复创建；全站通模式下三列写约定值「全站通」）
    const { error: packageError } = await supabase.from('user_packages').upsert({
      user_id: currentUser.id,
      role,
      organization: orderOrgName,
      category: orderCatName,
      level_text: orderLevelText,
      package_source: isTeacher ? 'teacher_purchase' : 'student_purchase',
      expire_date: `${expireDate.getFullYear()}-${String(expireDate.getMonth() + 1).padStart(2, '0')}-${String(expireDate.getDate()).padStart(2, '0')}`,
      is_active: true,
      duration_type: durationKey,
      order_id: currentOrder.id,
      price: currentOrder.price,
      actual_price: currentOrder.actualPrice || currentOrder.price,
      updated_at: getBeijingTimestamp(),
    }, { onConflict: 'order_id' });

    if (packageError) {
      console.error('创建/更新套餐失败:', packageError);
    }

    // 更新订单状态
    await supabase.from('orders').update({
      organization: orderOrgName,
      category: orderCatName,
      level_text: orderLevelText,
      status: 'paid',
      paid_at: getBeijingTimestamp(),
    }).eq('id', currentOrder.id);

    // 教师U币返利：如果学员已绑定教师，教师获得消费金额50%的等价U币
    if (!isTeacher && !alreadyPaid) {
      try {
        // 查询学员是否绑定了教师
        const { data: teacherStudent } = await supabase
          .from('teacher_students')
          .select('teacher_id')
          .eq('student_id', currentUser.id)
          .maybeSingle();

        if (teacherStudent?.teacher_id) {
          // 计算返利U币：实付金额 * 50% * 100（1元=100U币）
          const actualAmount = currentOrder.actualPrice || currentOrder.price;
          const rebatePoints = Math.floor(actualAmount * 0.5 * 100);

          if (rebatePoints > 0) {
            const session = await supabase.auth.getSession();
            await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.data.session?.access_token}`,
              },
              body: JSON.stringify({
                action: 'add_points',
                user_id: teacherStudent.teacher_id,
                role: 'teacher',
                points: rebatePoints,
                point_type: 'activity_reward',
                source_type: 'teacher_rebate',
                source_id: currentOrder.id,
                source_detail: {
                  order_no: currentOrder.orderNo,
                  student_id: currentUser.id,
                  actual_amount: actualAmount,
                  rebate_rate: 0.5,
                },
                remark: `学员消费返利：订单${currentOrder.orderNo}，消费¥${actualAmount.toFixed(2)}，返利50%（${rebatePoints}U币）`,
              }),
            });
          }
        }
      } catch (e) {
        console.error('教师U币返利失败:', e);
      }
    }

    // 发送消息通知（仅当订单之前不是 paid 状态时才发送）
    if (!alreadyPaid) {
      try {
        const session = await supabase.auth.getSession();
        await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.data.session?.access_token}`,
          },
          body: JSON.stringify({
            action: 'messages_trigger_scene',
            sceneType: 'order_paid',
            userId: currentUser.id,
            variables: {
              order_no: currentOrder.orderNo,
              amount: String(currentOrder.actualPrice || currentOrder.price),
            },
          }),
        });
      } catch {}
    }

    setShowAlipayModal(false);
    window.dispatchEvent(new Event('package-purchased'));
    onClose();
    navigate(`/payment-result?status=success&orderNo=${currentOrder.orderNo}&pointsDeducted=${currentOrder.pointsDeducted || 0}&deductionAmount=${currentOrder.deductionAmount || 0}`);
  };

  const handleAlipayFail = () => {
    setShowAlipayModal(false);
    navigate(`/payment-result?status=fail`);
  };

  const btnClass = isTeacher
    ? 'bg-purple-600 hover:bg-purple-700'
    : 'bg-blue-600 hover:bg-blue-700';
  const textClass = isTeacher ? 'text-purple-600' : 'text-blue-600';
  const borderClass = isTeacher ? 'border-purple-500 bg-purple-50' : 'border-blue-500 bg-blue-50';
  const hoverBorderClass = isTeacher ? 'hover:border-purple-300' : 'hover:border-blue-300';
  const iconBgClass = isTeacher ? 'bg-purple-100' : 'bg-blue-100';

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl p-6 w-[420px]">
          <div className="flex items-center justify-center py-12">
            <Loader2 className={`w-8 h-8 ${textClass} animate-spin`} />
            <span className="ml-2 text-gray-600">加载中...</span>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl p-6 w-[420px] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">购买刷题包</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5 text-gray-600" /></button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {!packagePrice && !siteWidePrices ? (
          <div className="text-center py-8 text-gray-400">
            <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>该级别暂无定价配置</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* 购买模式选项卡：当前级别 / 全站通 */}
            <div className="grid grid-cols-2 gap-2.5 mb-4">
              <button
                onClick={() => handleModeSwitch('level')}
                className={`relative flex flex-col items-center gap-0.5 py-3 px-3 rounded-xl border-2 transition-all ${
                  !isSiteWideMode
                    ? 'border-blue-600 bg-blue-600 shadow-md shadow-blue-200'
                    : 'border-blue-200 bg-blue-50 hover:border-blue-400 hover:bg-blue-100'
                }`}
              >
                <span className={`text-base font-bold ${!isSiteWideMode ? 'text-white' : 'text-blue-700'}`}>
                  单独购买{paper.level_text ? ` · ${paper.level_text}` : ''}
                </span>
                {!isSiteWideMode && (
                  <span className="text-[11px] text-blue-100 font-medium">单级别套餐</span>
                )}
              </button>
              <button
                onClick={() => handleModeSwitch('sitewide')}
                className={`relative flex flex-col items-center gap-0.5 py-3 px-3 rounded-xl border-2 transition-all ${
                  isSiteWideMode
                    ? 'border-amber-500 bg-gradient-to-b from-amber-400 to-amber-500 shadow-md shadow-amber-200'
                    : 'border-amber-300 bg-amber-50 hover:border-amber-500 hover:bg-amber-100'
                }`}
              >
                <span className={`text-base font-bold ${isSiteWideMode ? 'text-white' : 'text-amber-700'}`}>
                  👑 全站通
                </span>
                {isSiteWideMode ? (
                  <span className="text-[11px] text-amber-50 font-medium">全部试卷解锁</span>
                ) : (
                  <span className="text-[11px] text-amber-600 font-medium">更划算 · 不限级别</span>
                )}
              </button>
            </div>

            {/* 套餐信息 */}
            {isSiteWideMode ? (
              <p className="text-sm text-amber-600 font-medium mb-2">
                👑 全站通 · 不限考评体系/课程分类/级别，全站所有试卷均解锁
              </p>
            ) : (
              <p className="text-lg font-bold text-blue-700 mb-2 bg-blue-50 rounded-lg px-3 py-2">
                {paper.organization} · {categoryStore.getCategoryNameByCode(paper.category_type)} · {paper.level_text}
              </p>
            )}

            {/* 套餐选择 */}
            {durationConfigs.map(config => {
              const price = getPrice(config.key);
              const isSelected = selectedDuration === config.key;
              const swBorder = isSelected ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300';
              const swText = 'text-amber-600';
              const swIconBg = 'bg-amber-100';
              return (
                <button
                  key={config.key}
                  onClick={() => handleDurationSelect(config.key)}
                  className={`w-full p-3 border-2 rounded-xl flex items-center justify-between transition-all hover:shadow-md ${
                    isSiteWideMode ? swBorder : (isSelected ? borderClass : `border-gray-200 ${hoverBorderClass}`)
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${isSiteWideMode ? swIconBg : iconBgClass} rounded-lg flex items-center justify-center`}>
                      <config.icon className={`w-5 h-5 ${isSiteWideMode ? swText : textClass}`} />
                    </div>
                    <div className="text-left">
                      <span className="font-medium text-gray-800">{config.label}</span>
                      <p className="text-xs text-gray-500">{config.months}个月有效期</p>
                    </div>
                  </div>
                  <span className={`text-xl font-bold ${isSiteWideMode ? swText : textClass}`}>¥{price}</span>
                </button>
              );
            })}

            {/* U币抵扣区域 */}
            {selectedDuration && totalPoints > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-orange-200 bg-orange-50 rounded-lg p-3 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Coins className="w-4 h-4 text-orange-500" />
                    <span className="text-sm font-medium text-orange-700">U币抵扣</span>
                  </div>
                  <button
                    onClick={() => {
                      setUsePoints(!usePoints);
                      if (!usePoints) {
                        const maxPointsForOrder = Math.min(totalPoints, Math.floor(currentPrice * POINTS_PER_YUAN));
                        setPointsToUse(maxPointsForOrder);
                      } else {
                        setPointsToUse(0);
                      }
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      usePoints ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {usePoints ? '已开启' : '未开启'}
                  </button>
                </div>

                {usePoints && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>可用U币: {totalPoints}分 (≈¥{maxDeductionYuan})</span>
                      <span>使用: {pointsToUse}分 (≈¥{pointsToUse / POINTS_PER_YUAN})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => adjustPoints(-100)}
                        className="w-8 h-8 flex items-center justify-center bg-white border rounded hover:bg-gray-50"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <input
                        type="number"
                        value={pointsToUse}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          const maxPointsForOrder = Math.min(totalPoints, Math.floor(currentPrice * POINTS_PER_YUAN));
                          setPointsToUse(Math.max(0, Math.min(maxPointsForOrder, val)));
                        }}
                        className="flex-1 text-center text-sm border rounded px-2 py-1"
                        step={100}
                        min={0}
                        max={Math.min(totalPoints, Math.floor(currentPrice * POINTS_PER_YUAN))}
                      />
                      <button
                        onClick={() => adjustPoints(100)}
                        className="w-8 h-8 flex items-center justify-center bg-white border rounded hover:bg-gray-50"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => {
                          const maxPointsForOrder = Math.min(totalPoints, Math.floor(currentPrice * POINTS_PER_YUAN));
                          setPointsToUse(maxPointsForOrder);
                        }}
                        className="px-2 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600"
                      >
                        全额
                      </button>
                    </div>
                  </div>
                )}

                {/* 价格明细 */}
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>原价</span>
                    <span>¥{currentPrice}</span>
                  </div>
                  {usePoints && pointsToUse > 0 && (
                  <div className="flex justify-between text-orange-600">
                    <span>U币抵扣</span>
                    <span>-¥{deductionAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold border-t pt-1">
                  <span>实付金额</span>
                  <span className={isZeroPayment ? 'text-green-600' : textClass}>
                    {isZeroPayment ? '¥0 (U币全额抵扣)' : `¥${actualPrice.toFixed(2)}`}
                  </span>
                </div>
                </div>
              </motion.div>
            )}

            {/* 购买按钮 */}
            {selectedDuration && (
              <button
                onClick={handlePurchase}
                disabled={paying || zeroPaymentProcessing}
                className={`w-full py-3 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2 transition-colors ${btnClass}`}
              >
                {zeroPaymentProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    U币支付中...
                  </>
                ) : paying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    创建订单中...
                  </>
                ) : isZeroPayment ? (
                  'U币全额支付'
                ) : (
                  `支付 ¥${actualPrice.toFixed(2)}`
                )}
              </button>
            )}
          </div>
        )}
      </motion.div>

      {currentOrder && !isZeroPayment && (
        <AlipayPaymentModal
          isOpen={showAlipayModal}
          onClose={() => setShowAlipayModal(false)}
          orderId={currentOrder?.id || ''}
          orderNo={currentOrder?.orderNo || ''}
          amount={currentOrder?.actualPrice || currentOrder?.price || 0}
          subject={isSiteWideMode ? '全站通刷题包' : `${paper.organization} ${categoryStore.getCategoryNameByCode(paper.category_type)} ${paper.level_text}`}
          onSuccess={handleAlipaySuccess}
          onFail={handleAlipayFail}
        />
      )}
    </div>
  );
}
